class BrandHeroSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.track = this.querySelector('[data-hero-track]');
    this.slides = [...this.querySelectorAll('[data-hero-slide]')];
    this.dots = [...this.querySelectorAll('[data-hero-dot]')];
    this.previous = this.querySelector('[data-hero-previous]');
    this.next = this.querySelector('[data-hero-next]');
    if (!this.track || this.slides.length < 2) return;

    this.initialized = true;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.previous?.addEventListener('click', this.showPrevious, { signal });
    this.next?.addEventListener('click', this.showNext, { signal });
    this.track.addEventListener('scroll', this.onScroll, { passive: true, signal });
    this.track.addEventListener('touchstart', this.onTouchStart, { passive: true, signal });
    this.track.addEventListener('touchend', this.onTouchEnd, { passive: true, signal });
    this.addEventListener('keydown', this.onKeydown, { signal });
    for (const dot of this.dots) dot.addEventListener('click', this.onDotClick, { signal });
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.track);
    this.update(0);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
    this.initialized = false;
  }

  index = 0;
  showPrevious = () => this.goTo(this.index - 1);
  showNext = () => this.goTo(this.index + 1);
  onDotClick = (event) => this.goTo(Number(event.currentTarget.dataset.heroDot));

  onKeydown = (event) => {
    const dot = event.target.closest('[data-hero-dot]');
    if (event.target !== this.track && !dot) return;
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    event.preventDefault();
    let nextIndex = this.index;
    if (event.key === 'ArrowLeft') nextIndex -= 1;
    if (event.key === 'ArrowRight') nextIndex += 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.slides.length - 1;
    this.goTo(nextIndex);
    if (dot) this.dots[this.index]?.focus();
  };

  onScroll = () => {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.update(this.closestIndex()));
  };

  onTouchStart = (event) => {
    if (event.touches.length !== 1) return;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.touchStartIndex = this.index;
  };

  onTouchEnd = (event) => {
    if (this.touchStartX === undefined || event.changedTouches.length !== 1) return;
    const deltaX = event.changedTouches[0].clientX - this.touchStartX;
    const deltaY = event.changedTouches[0].clientY - this.touchStartY;
    this.touchStartX = undefined;
    this.touchStartY = undefined;
    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    this.goTo(this.touchStartIndex + (deltaX < 0 ? 1 : -1));
  };

  onResize = () => this.goTo(this.index, false);

  closestIndex() {
    let closest = 0;
    let distance = Number.POSITIVE_INFINITY;
    for (const [index, slide] of this.slides.entries()) {
      const current = Math.abs(slide.offsetLeft - this.track.scrollLeft);
      if (current >= distance) continue;
      closest = index;
      distance = current;
    }
    return closest;
  }

  goTo(index, smooth = true) {
    const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: this.slides[nextIndex].offsetLeft, behavior: smooth && !reduced ? 'smooth' : 'auto' });
    this.update(nextIndex);
  }

  update(index) {
    this.index = Math.max(0, Math.min(index, this.slides.length - 1));
    if (this.previous) this.previous.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index === this.slides.length - 1;
    for (const [dotIndex, dot] of this.dots.entries()) {
      dot.toggleAttribute('aria-current', dotIndex === this.index);
    }
    for (const [slideIndex, slide] of this.slides.entries()) {
      const inactive = slideIndex !== this.index;
      slide.setAttribute('aria-hidden', String(inactive));
      slide.inert = inactive;
      if (inactive) for (const video of slide.querySelectorAll('video')) video.pause();
    }
  }
}

if (!customElements.get('brand-hero-slider')) customElements.define('brand-hero-slider', BrandHeroSlider);
